import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

export interface UseVideoPlayerOptions {
  src: string | null;
  type?: string | null;
  poster?: string | null;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  paused?: boolean;
  onPlay?: () => void;
  onPause?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onEnded?: () => void;
}

export interface UseVideoPlayerResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLoading: boolean;
}

const isHlsSource = (src: string, type?: string | null): boolean => {
  if (
    type === "application/x-mpegURL" ||
    type === "application/vnd.apple.mpegurl"
  ) {
    return true;
  }
  return src.includes(".m3u8");
};

/**
 * hls.js + ネイティブ <video> で動画再生を管理するフック。
 * イベントリスナーをソースのセットより先に登録し、ロード完了が爆速な場合でも
 * レースコンディションで isLoading が true のままになるのを防ぎます。
 */
export const useVideoPlayer = ({
  src,
  type,
  poster,
  autoplay = true,
  loop = true,
  muted = true,
  paused = false,
  onPlay,
  onPause,
  onTimeUpdate,
  onEnded,
}: UseVideoPlayerOptions): UseVideoPlayerResult => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // コールバックが親コンポーネントでメモライズされていない場合でも
  // Effectが頻繁に再実行されて動画がリセットされるのを防ぐため、Refで保持します。
  const callbacksRef = useRef({ onPlay, onPause, onTimeUpdate, onEnded });
  useEffect(() => {
    callbacksRef.current = { onPlay, onPause, onTimeUpdate, onEnded };
  }, [onPlay, onPause, onTimeUpdate, onEnded]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // ソースがない場合は、ローディング状態に戻してソースをクリア
    if (!src) {
      el.removeAttribute("src");
      el.load();
      setIsLoading(true);
      return;
    }

    setIsLoading(true);

    hlsRef.current?.destroy();
    hlsRef.current = null;

    if (poster) {
      el.poster = poster;
    } else {
      el.removeAttribute("poster");
    }

    el.loop = loop;
    el.muted = muted;

    // 1. まず先にイベントリスナーを登録（ロード完了イベントを逃さないため）
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handlePlaying = () => setIsLoading(false);
    const handlePlay = () => callbacksRef.current.onPlay?.();
    const handlePause = () => callbacksRef.current.onPause?.();
    const handleTimeUpdate = () => {
      if (callbacksRef.current.onTimeUpdate) {
        callbacksRef.current.onTimeUpdate(el.currentTime, el.duration);
      }
    };
    const handleEnded = () => callbacksRef.current.onEnded?.();

    el.addEventListener("waiting", handleWaiting);
    el.addEventListener("canplay", handleCanPlay);
    el.addEventListener("playing", handlePlaying);
    el.addEventListener("play", handlePlay);
    el.addEventListener("pause", handlePause);
    el.addEventListener("timeupdate", handleTimeUpdate);
    el.addEventListener("ended", handleEnded);

    // 2. その後でソースをセットしてロード開始
    if (isHlsSource(src, type)) {
      if (Hls.isSupported()) {
        const hls = new Hls({ autoStartLoad: true });
        hls.loadSource(src);
        hls.attachMedia(el);
        hlsRef.current = hls;
      } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
        el.src = src;
      }
    } else {
      el.src = src;
    }

    // 3. 再生状態の制御
    if (paused) {
      el.pause();
    } else {
      const playPromise = el.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          el.muted = true;
          el.play().catch(() => {});
        });
      }
    }

    return () => {
      el.removeEventListener("waiting", handleWaiting);
      el.removeEventListener("canplay", handleCanPlay);
      el.removeEventListener("playing", handlePlaying);
      el.removeEventListener("play", handlePlay);
      el.removeEventListener("pause", handlePause);
      el.removeEventListener("timeupdate", handleTimeUpdate);
      el.removeEventListener("ended", handleEnded);

      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [src, type, poster, autoplay, loop, muted, paused]);

  return { videoRef, isLoading };
};
